import { supabase } from "@/platform/lib/supabase";
import type { PlatformLLMCallRow, PlatformTenantSlug } from "@/platform/lib/database.types";

/**
 * Agregação diária de custos LLM por tenant + função.
 * Admin-only via RLS de platform_llm_calls.
 */
export interface DailyLlmStats {
  day: string;
  tenant: PlatformTenantSlug | null;
  function_name: string;
  calls: number;
  total_tokens: number;
  total_cost_usd: number;
  avg_latency_ms: number;
}

export async function fetchRecentLlmCalls(limit = 100): Promise<PlatformLLMCallRow[]> {
  const { data, error } = await supabase
    .from("platform_llm_calls")
    .select()
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as PlatformLLMCallRow[];
}

/**
 * Agregação simples client-side a partir das últimas N chamadas.
 * Para volume real (>10k calls) usar uma view materializada Postgres.
 */
export async function fetchLlmSummaryByTenant(): Promise<
  Array<{
    tenant: PlatformTenantSlug | "unknown";
    calls: number;
    cost_usd: number;
    cost_brl: number;
    avg_latency_ms: number;
    errors: number;
  }>
> {
  const { data, error } = await supabase
    .from("platform_llm_calls")
    .select("tenant, cost_usd, latency_ms, status")
    .order("created_at", { ascending: false })
    .limit(1000);
  if (error) throw error;

  const groups = new Map<
    string,
    { calls: number; cost_usd: number; latencies: number[]; errors: number }
  >();
  for (const row of data ?? []) {
    const tenant = (row.tenant as PlatformTenantSlug | null) ?? "unknown";
    const g = groups.get(tenant) ?? { calls: 0, cost_usd: 0, latencies: [], errors: 0 };
    g.calls++;
    g.cost_usd += Number(row.cost_usd ?? 0);
    if (row.latency_ms) g.latencies.push(Number(row.latency_ms));
    if (row.status && row.status !== "ok") g.errors++;
    groups.set(tenant, g);
  }

  return Array.from(groups.entries()).map(([tenant, g]) => ({
    tenant: tenant as PlatformTenantSlug | "unknown",
    calls: g.calls,
    cost_usd: Number(g.cost_usd.toFixed(4)),
    cost_brl: Number((g.cost_usd * 5).toFixed(2)),
    avg_latency_ms:
      g.latencies.length > 0
        ? Math.round(g.latencies.reduce((a, b) => a + b, 0) / g.latencies.length)
        : 0,
    errors: g.errors,
  }));
}

export async function fetchLlmSummaryByFunction(): Promise<
  Array<{
    function_name: string;
    calls: number;
    cost_usd: number;
    cost_brl: number;
    avg_latency_ms: number;
  }>
> {
  const { data, error } = await supabase
    .from("platform_llm_calls")
    .select("function_name, cost_usd, latency_ms")
    .order("created_at", { ascending: false })
    .limit(1000);
  if (error) throw error;

  const groups = new Map<string, { calls: number; cost_usd: number; latencies: number[] }>();
  for (const row of data ?? []) {
    const fn = row.function_name as string;
    const g = groups.get(fn) ?? { calls: 0, cost_usd: 0, latencies: [] };
    g.calls++;
    g.cost_usd += Number(row.cost_usd ?? 0);
    if (row.latency_ms) g.latencies.push(Number(row.latency_ms));
    groups.set(fn, g);
  }

  return Array.from(groups.entries())
    .map(([fn, g]) => ({
      function_name: fn,
      calls: g.calls,
      cost_usd: Number(g.cost_usd.toFixed(4)),
      cost_brl: Number((g.cost_usd * 5).toFixed(2)),
      avg_latency_ms:
        g.latencies.length > 0
          ? Math.round(g.latencies.reduce((a, b) => a + b, 0) / g.latencies.length)
          : 0,
    }))
    .sort((a, b) => b.cost_usd - a.cost_usd);
}
